import {EnumScheduleRepeatType, IChannelItem, IScheduleEvent} from "../interfaces";
import {MAX_PLAYLIST_TIME} from "./global-constants";
import {dateAfter, dateToString} from "./date";
import {buildScheduleTimelineState, parseScheduleEventValue, SCHEDULE_WEEK_BITS} from "./schedule-timeline";

/**
 * Schedule insights: what a schedule's rules (its events) will actually play, summarised for the band that docks
 * under the scene canvas when a Scheduler block is selected (scheduler-insights). Pure functions over the store's
 * IScheduleEvent rows; the day expansion reuses the same builder the scheduler timeline renders from, so
 * "plays today" here is exactly what the timeline would draw.
 */

export type ScheduleRuleKind = 'scene' | 'resource' | 'event';

// repeat types in legend order (daily first, like the scheduler page legend). The index is the rule's categorical
// color slot (--si-cat-<slot> in the band's styles): a fixed, validated order, never assigned by rank.
export const SCHEDULE_REPEAT_ORDER: number[] = [
    EnumScheduleRepeatType.DAILY,
    EnumScheduleRepeatType.HOURLY,
    EnumScheduleRepeatType.WEEKLY,
    EnumScheduleRepeatType.MONTHLY,
    EnumScheduleRepeatType.YEARLY,
    EnumScheduleRepeatType.ONCE
];

export const SCHEDULE_REPEAT_LABEL_KEYS: {[repeatType: number]: string} = {
    [EnumScheduleRepeatType.ONCE]: 'general.once',
    [EnumScheduleRepeatType.YEARLY]: 'general.yearly',
    [EnumScheduleRepeatType.MONTHLY]: 'general.monthly',
    [EnumScheduleRepeatType.WEEKLY]: 'general.weekly',
    [EnumScheduleRepeatType.HOURLY]: 'general.hourly',
    [EnumScheduleRepeatType.DAILY]: 'general.daily'
};

export interface IRuleSegment {
    start: number;   // seconds from midnight, clipped to the day
    end: number;
}

export interface IRuleInsight {
    id: string;
    event: IScheduleEvent;
    name: string;                 // event_value.name, else event_name, else a default
    kind: ScheduleRuleKind;
    targetId: string;             // scene / resource id the rule plays (event_value.id)
    repeatType: number;
    slot: number;                 // categorical color slot = index in SCHEDULE_REPEAT_ORDER
    startSec: number;             // time of day the rule starts (hourly: minutes past each hour, as seconds)
    durationSec: number;
    occurrencesToday: number;
    playSecondsToday: number;
    playsToday: boolean;
    segments: IRuleSegment[];     // today's blocks
    weekdays: boolean[];          // Sun..Sat flags (weekly rules only, else [])
    next7: boolean[];             // plays on each of the 7 days starting with the reference day
    startDate: string;            // yyyy-mm-dd or ''
    endDate: string;              // yyyy-mm-dd or '' (forever / unset)
    forever: boolean;
    share: number;                // playSecondsToday relative to the busiest rule (0..1)
}

export interface IScheduleInsights {
    referenceDay: string;         // yyyy-mm-dd the "today" numbers refer to
    rules: IRuleInsight[];
    counts: {rules: number, scenes: number, resources: number, events: number, playingToday: number};
    repeatCounts: Array<{repeatType: number, slot: number, count: number}>;   // only the types present, legend order
    totalPlaySecondsToday: number;
    coverageToday: number;        // 0..1 share of the day with at least one rule playing
    busiestRuleId: string;
}

export interface INextRun {
    day: string;                  // yyyy-mm-dd
    startSec: number;
}

const clampDay = (v: number) => Math.max(0, Math.min(MAX_PLAYLIST_TIME, v));

export function normalizeRuleKind(type: any): ScheduleRuleKind {
    return type === 'resource' || type === 'event' ? type : 'scene';
}

export function computeScheduleInsights(events: IScheduleEvent[], referenceDate: Date = new Date()): IScheduleInsights {
    const list = events || [];
    const days: string[] = [];
    for (let i = 0; i < 7; i++) {
        days.push(dateToString(dateAfter(referenceDate, {days: i})));
    }
    const week = buildScheduleTimelineState({events: list, dateArray: days, zoom: 1});

    const todayBlocks = new Map<string, IChannelItem[]>();
    const weekFlags = new Map<string, boolean[]>();
    week.items.forEach((block) => {
        const id = String(block.eventId);
        if (block.channel === 0) {
            if (!todayBlocks.has(id)) todayBlocks.set(id, []);
            todayBlocks.get(id).push(block);
        }
        if (!weekFlags.has(id)) weekFlags.set(id, [false, false, false, false, false, false, false]);
        weekFlags.get(id)[block.channel] = true;
    });

    const rules: IRuleInsight[] = list.map((ev) => {
        const value = parseScheduleEventValue(ev.event_value) || {};
        const repeatType = Math.floor(Number(ev.repeat_type) || 0);
        // rows loaded from the server can carry numeric strings - coerce before any arithmetic
        const segments: IRuleSegment[] = (todayBlocks.get(String(ev.schedule_event_id)) || [])
            .map((b) => {
                const start = Number(b.start) || 0;
                const duration = Number(b.duration) || 0;
                return {start: clampDay(start), end: clampDay(start + duration)};
            })
            .filter((s) => s.end > s.start)
            .sort((a, b) => a.start - b.start);
        const playSecondsToday = segments.reduce((sum, s) => sum + (s.end - s.start), 0);
        const weekdays = repeatType === EnumScheduleRepeatType.WEEKLY
            // tslint:disable-next-line
            ? SCHEDULE_WEEK_BITS.map((bit) => (bit & (Number(ev.weekly_days) || 0)) > 0)
            : [];
        return {
            id: String(ev.schedule_event_id),
            event: ev,
            name: value.name || ev.event_name || 'new event',
            kind: normalizeRuleKind(value.type),
            targetId: value.id != null ? String(value.id) : '',
            repeatType,
            slot: Math.max(0, SCHEDULE_REPEAT_ORDER.indexOf(repeatType)),
            startSec: repeatType === EnumScheduleRepeatType.HOURLY ? (Number(ev.hourly_offset) || 0) : (Number(ev.time) || 0),
            durationSec: Number(ev.duration) || 0,
            occurrencesToday: segments.length,
            playSecondsToday,
            playsToday: segments.length > 0,
            segments,
            weekdays,
            next7: weekFlags.get(String(ev.schedule_event_id)) || [false, false, false, false, false, false, false],
            startDate: ev.start_date ? dateToString(ev.start_date, true) : '',
            endDate: ev.end_date && !ev.repeat_forever ? dateToString(ev.end_date, true) : '',
            forever: !!ev.repeat_forever,
            share: 0
        };
    });

    // bar lengths are relative to the busiest rule
    let busiest: IRuleInsight = null;
    rules.forEach((r) => {
        if (!busiest || r.playSecondsToday > busiest.playSecondsToday) busiest = r;
    });
    const maxPlay = busiest ? busiest.playSecondsToday : 0;
    rules.forEach((r) => r.share = maxPlay > 0 ? r.playSecondsToday / maxPlay : 0);

    // day coverage = union of every block today
    const all = rules.reduce((acc, r) => acc.concat(r.segments), [] as IRuleSegment[]).sort((a, b) => a.start - b.start);
    let covered = 0;
    let curStart = -1;
    let curEnd = -1;
    all.forEach((s) => {
        if (s.start > curEnd) {
            if (curEnd > curStart) covered += curEnd - curStart;
            curStart = s.start;
            curEnd = s.end;
        } else if (s.end > curEnd) {
            curEnd = s.end;
        }
    });
    if (curEnd > curStart) covered += curEnd - curStart;

    const repeatCounts = SCHEDULE_REPEAT_ORDER
        .map((repeatType, slot) => ({repeatType, slot, count: rules.filter((r) => r.repeatType === repeatType).length}))
        .filter((c) => c.count > 0);

    return {
        referenceDay: days[0].slice(0, 10),
        rules,
        counts: {
            rules: rules.length,
            scenes: rules.filter((r) => r.kind === 'scene').length,
            resources: rules.filter((r) => r.kind === 'resource').length,
            events: rules.filter((r) => r.kind === 'event').length,
            playingToday: rules.filter((r) => r.playsToday).length
        },
        repeatCounts,
        totalPlaySecondsToday: rules.reduce((sum, r) => sum + r.playSecondsToday, 0),
        coverageToday: Math.min(1, covered / MAX_PLAYLIST_TIME),
        busiestRuleId: busiest && busiest.playSecondsToday > 0 ? busiest.id : null
    };
}

// the next time a rule starts after `from` (later today, or on the next day it plays), scanning up to maxDays ahead
export function nextRuleRun(event: IScheduleEvent, from: Date = new Date(), maxDays = 400): INextRun {
    if (!event) return null;
    const nowSec = from.getHours() * 3600 + from.getMinutes() * 60 + from.getSeconds();
    for (let d = 0; d <= maxDays; d++) {
        const day = dateToString(dateAfter(from, {days: d}));
        const blocks = buildScheduleTimelineState({events: [event], dateArray: [day], zoom: 1}).items
            .map((b) => Number(b.start) || 0)
            .filter((start) => d > 0 || start > nowSec)
            .sort((a, b) => a - b);
        if (blocks.length) {
            return {day: day.slice(0, 10), startSec: blocks[0]};
        }
    }
    return null;
}

// 5400 -> "1h 30m", 2700 -> "45m", 30 -> "30s", 0 -> "0m"
export function formatPlaySeconds(sec: number): string {
    const s = Math.max(0, Math.floor(Number(sec) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
    if (m > 0) return `${m}m`;
    return `${s}s`;
}

// 30600 -> "08:30"; 86400 -> "24:00"
export function formatClock(sec: number): string {
    const s = Math.max(0, Math.min(MAX_PLAYLIST_TIME, Math.floor(Number(sec) || 0)));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h < 10 ? '0' + h : h}:${m < 10 ? '0' + m : m}`;
}
